import {
  partialRight,
  path as ramdaPath,
} from 'ramda';

import {
  FETCH_MASTER_ACCOUNTS,
  FETCH_MASTER_ACCOUNTS_SUCCESS,
  FETCH_MASTER_ACCOUNTS_ERROR,
  CHECK_MASTER_ACCOUNTS_PROHIBITION,
  CHECK_MASTER_ACCOUNTS_PROHIBITION_SUCCESS,
  CHECK_MASTER_ACCOUNTS_PROHIBITION_ERROR,
  UPDATE_MASTER_ACCOUNTS_PROHIBITION,
  UPDATE_MASTER_ACCOUNTS_PROHIBITION_SUCCESS,
  UPDATE_MASTER_ACCOUNTS_PROHIBITION_ERROR,
  FETCH_ASSOCIATED_ACCOUNTS,
  FETCH_ASSOCIATED_ACCOUNTS_SUCCESS,
  FETCH_ASSOCIATED_ACCOUNTS_ERROR,
  CREATE_ACCOUNT_LINK,
  CREATE_ACCOUNT_LINK_SUCCESS,
  CREATE_ACCOUNT_LINK_ERROR,
  RESET_ACCOUNT_LINKING_STATUS,
  DELETE_ACCOUNT_LINK,
  DELETE_ACCOUNT_LINK_SUCCESS,
  DELETE_ACCOUNT_LINK_ERROR,
  DELETE_MASTER_ACCOUNT_LINK,
  DELETE_MASTER_ACCOUNT_LINK_SUCCESS,
  DELETE_MASTER_ACCOUNT_LINK_ERROR,
  SEND_ACCOUNT_LINK_REQUEST_SUCCESS,
  CANCEL_LINKAGE_REQUEST,
  CANCEL_LINKAGE_REQUEST_SUCCESS,
  CANCEL_LINKAGE_REQUEST_ERROR,
  FETCH_LINKAGE_REQUESTS,
  FETCH_LINKAGE_REQUESTS_SUCCESS,
  FETCH_LINKAGE_REQUESTS_ERROR,
  FETCH_LINKAGE_REQUESTS_FOR_MASTER,
  FETCH_LINKAGE_REQUESTS_FOR_MASTER_SUCCESS,
  FETCH_LINKAGE_REQUESTS_FOR_MASTER_ERROR,
  CANCEL_LINKAGE_REQUEST_FOR_MASTER_SUCCESS,
  CANCEL_LINKAGE_REQUEST_FOR_MASTER,
  UPDATE_LINKAGE_REQUEST,
  UPDATE_LINKAGE_REQUEST_SUCCESS,
  UPDATE_LINKAGE_REQUEST_ERROR,
  UPDATE_LINKAGE_REQUEST_FOR_MASTER,
  UPDATE_LINKAGE_REQUEST_FOR_MASTER_SUCCESS,
  FETCH_OFFER,
  FETCH_OFFER_SUCCESS,
  FETCH_OFFER_ERROR,
  TOGGLE_OFFER_POPUP,
  FETCH_LOGIN_USER_TYPES,
  FETCH_LOGIN_USER_TYPES_SUCCESS,
  FETCH_LOGIN_USER_TYPES_ERROR,
  RESET_LOGIN_USER_TYPES,
  RESET_ERROR,
  UPDATE_ASSOCIATED_ACCOUNT,
  UPDATE_ASSOCIATED_ACCOUNT_ERROR,
  UPDATE_ASSOCIATED_CTN_ACCOUNT_SUCCESS,
  UPDATE_ASSOCIATED_BAN_ACCOUNT_SUCCESS,
  UPDATE_ASSOCIATED_BEN_ACCOUNT_SUCCESS,
  SET_MASTER_ACCOUNT_AS_DEFAULT,
  SET_MASTER_ACCOUNT_AS_DEFAULT_SUCCESS,
  SET_MASTER_ACCOUNT_AS_DEFAULT_ERROR,
  RESET_ASSOCIATED_ACCOUNTS_ERROR,
  RESET_MASTER_ACCOUNTS_ERROR,
  FETCH_PIN_PUK,
  FETCH_PIN_PUK_SUCCESS,
  FETCH_PIN_PUK_ERROR,
} from './constants';

const initialState = {
  error: null,
  associatedAccountsError: null,
  masterAccountsError: null,
  masterAccounts: null,
  associatedAccounts: null,
  isMasterAccountsEnabled: null,
  isMasterAccountsFetching: false,
  isAssociatedAccountsFetching: false,
  isMasterAccountsProhibitionChecking: false,
  isMasterAccountsProhibitionUpdating: false,
  isAccountLinkDeleting: false,
  isMasterAccountLinkDeleting: false,
  isAccountLinkCanceling: false,
  isLinkageRequestsFetching: false,
  isLinkageRequestsForMasterFetching: false,
  isLinkageRequestUpdating: false,
  isOfferFetching: false,
  isOfferPopupOpened: false,
  isLoginUserTypesFetching: false,
  isAssociatedAccountUpdating: false,
  ssoLoginDefault: false,
  isDefaultSsoLoginUpdating: false,
  linkageRequests: {},
  offer: null,
  loginUserTypes: null,
  accountLinkingStatus: {
    isUpdating: false,
    isSent: false,
    success: false,
    error: null,
  },
  isPinPukFetching: false,
  pinPuk: null,
  pinPukFetchingError: null,
};

const isObject = (value) => typeof value === 'object';

const isFunction = (value) => typeof value === 'function';

const deleteMasterAccountLink = (state, deletedLogin) => ({
  ...state,
  masterAccounts: state.masterAccounts
    .filter(({ login }) => login !== deletedLogin),
});

const cancelLinkageRequest = (state, login, requestProp, userNameProp) => ({
  ...state,
  linkageRequests: {
    ...state.linkageRequests,
    [requestProp]: state.linkageRequests[requestProp]
      .filter((request) => request[userNameProp] !== login),
  },
});

const cancelOutgoingLinkageRequestForChild = partialRight(cancelLinkageRequest,
  ['outgoingLinkageRequests', 'destinationName']);

const cancelOutgoingLinkageRequestForMaster = partialRight(cancelLinkageRequest,
  ['outgoingLinkageRequestsForMaster', 'destinationName']);

const cancelIncomingLinkageRequestForChild = partialRight(cancelLinkageRequest,
  ['incomingLinkageRequests', 'initiatorName']);

const cancelIncomingLinkageRequestForMaster = partialRight(cancelLinkageRequest,
  ['incomingLinkageRequestsForMaster', 'initiatorName']);

const startCancelingLinkageRequest = (state, login, requestProp, userNameProp) => ({
  ...state,
  linkageRequests: {
    ...state.linkageRequests,
    [requestProp]: state.linkageRequests[requestProp]
      .map((request) => (request[userNameProp] === login ? {
        ...request,
        isCanceling: true,
      } : request)),
  },
});

const startCancelingOutgoingLinkageRequestForChild = partialRight(startCancelingLinkageRequest,
  ['outgoingLinkageRequests', 'destinationName']);

const startCancelingOutgoingLinkageRequestForMaster = partialRight(startCancelingLinkageRequest,
  ['outgoingLinkageRequestsForMaster', 'destinationName']);

const startCancelingIncomingLinkageRequestForChild = partialRight(startCancelingLinkageRequest,
  ['incomingLinkageRequests', 'initiatorName']);

const startCancelingIncomingLinkageRequestForMaster = partialRight(startCancelingLinkageRequest,
  ['incomingLinkageRequestsForMaster', 'initiatorName']);

const immutableUpdate = (data, path, updater) => {
  if (!path || !path.length) {
    return isFunction(updater) ? updater(data) : updater;
  }

  const prop = path.shift();

  return Array.isArray(data)
    ? data.map((item, index) => (index.toString() === prop.toString()
      ? immutableUpdate(item, path, updater) : item))
    : ({
      ...data,
      [prop]: immutableUpdate(data[prop], path, updater),
    });
};

const getPathToCtn = (account, predicate, path = []) => {
  let newPath = null;

  if (predicate(account)) {
    return path;
  }

  Object.keys(account).some((key) => {
    newPath = isObject(account[key])
              && getPathToCtn(account[key], predicate, [...path, key]);

    return !!newPath;
  });

  return newPath;
};

const updateAssociatedCtnAccount = (state, data) => {
  let defaultSsoLoginShouldBeUpdated = false;

  const updater = (oldValue) => {
    if (!oldValue.ssoLoginDefault && data.isDefaultSsoLogin) {
      defaultSsoLoginShouldBeUpdated = true;
    }

    return {
      ...oldValue,
      nickName: data.nickName,
      ssoLoginDefault: data.isDefaultSsoLogin,
      postpaidLimit: oldValue.isPostpaid ? {
        balance: data.postpaidLimit,
      } : oldValue.postpaidLimit,
    };
  };

  const path = getPathToCtn(state.associatedAccounts, (account) => account.login === data.linkedAccountLogin);

  if (path) {
    const associatedAccounts = immutableUpdate(state.associatedAccounts, path, updater);

    if (defaultSsoLoginShouldBeUpdated) {
      const pathToOldDefaultSsoLogin = getPathToCtn(
        state.associatedAccounts,
        (account) => account.ssoLoginDefault,
      );

      if (pathToOldDefaultSsoLogin) {
        return {
          ...state,
          associatedAccounts: immutableUpdate(
            associatedAccounts, pathToOldDefaultSsoLogin,
            (oldValue) => ({
              ...oldValue,
              ssoLoginDefault: false,
            }),
          ),
        };
      }

      return {
        ...state,
        associatedAccounts,
        ssoLoginDefault: false,
      };
    }

    return {
      ...state,
      associatedAccounts,
    };
  }

  return state;
};

const getBanIndex = (accounts, ban) => accounts
  .findIndex((account) => account.ban === ban);

const updateAssociatedBanAccount = (state, data) => {
  const updater = (oldValue) => ({
    ...oldValue,
    nickName: data.nickName,
    postpaidLimit: oldValue.isPostpaid ? {
      balance: data.postpaidLimit,
      notificationCtn: data.postpaidNotificationCtn,
    } : oldValue.postpaidLimit,
  });

  let index = getBanIndex(state.associatedAccounts.bans, data.ban);
  let accountType = 'bans';

  if (index === -1) {
    index = getBanIndex(state.associatedAccounts.bens, data.ban);
    accountType = 'bens';
  }

  if (index !== -1) {
    return immutableUpdate(state, ['associatedAccounts', accountType, index], updater);
  }

  return state;
};

const updateAssociatedBenAccount = (state, data) => {
  const updater = (oldValue) => ({
    ...oldValue,
    invoiceEmail: data.invoiceEmail,
    emailRegular: data.emailRegular,
  });
  const { bans } = state.associatedAccounts;
  const banIndex = bans
    .findIndex(({ ban }) => ban === data.ban);
  const ban = bans[banIndex];

  if (ban) {
    const benIndex = ban.bens
      .findIndex(({ ben }) => ben === data.ben);

    if (ban.bens[benIndex]) {
      return immutableUpdate(
        state,
        ['associatedAccounts', 'bans', banIndex, 'bens', benIndex], updater,
      );
    }
  }

  return state;
};

const hasDefaultSsoLogin = (account) => account.ssoLoginDefault
  || Object.keys(account).some((key) => isObject(account[key]) && hasDefaultSsoLogin(account[key]));

const resetDefaultSsoLoginForAssociatedAccounts = (state) => {
  const updater = (oldValue) => ({
    ...oldValue,
    ssoLoginDefault: false,
  });
  const path = getPathToCtn(
    state.associatedAccounts,
    (account) => account.ssoLoginDefault,
  );

  if (path) {
    return {
      ...state,
      associatedAccounts: immutableUpdate(state.associatedAccounts, path, updater),
    };
  }

  return state;
};

const deleteAccountLink = (state, login) => {
  const path = getPathToCtn(
    state.associatedAccounts,
    (account) => account.login === login,
  );

  if (path) {
    const { ssoLoginDefault } = ramdaPath(path, state.associatedAccounts);
    const accountIndex = path.pop();

    return {
      ...state,
      ssoLoginDefault: ssoLoginDefault || state.ssoLoginDefault,
      associatedAccounts: immutableUpdate(
        state.associatedAccounts, path,
        (oldValue) => oldValue.filter((element, index) => index.toString() !== accountIndex.toString()),
      ),
    };
  }

  return state;
};

export default function (state = initialState, action) {
  switch (action.type) {
    case FETCH_MASTER_ACCOUNTS:
      return {
        ...state,
        isMasterAccountsFetching: true,
      };
    case FETCH_MASTER_ACCOUNTS_SUCCESS:
      return {
        ...state,
        isMasterAccountsFetching: false,
        masterAccounts: action.data,
      };
    case FETCH_MASTER_ACCOUNTS_ERROR:
      return {
        ...state,
        isMasterAccountsFetching: false,
        masterAccountsError: action.error,
        masterAccounts: null,
      };
    case CHECK_MASTER_ACCOUNTS_PROHIBITION:
      return {
        ...state,
        isMasterAccountsProhibitionChecking: true,
      };
    case CHECK_MASTER_ACCOUNTS_PROHIBITION_SUCCESS:
      return {
        ...state,
        isMasterAccountsProhibitionChecking: false,
        isMasterAccountsEnabled: action.data,
      };
    case CHECK_MASTER_ACCOUNTS_PROHIBITION_ERROR:
      return {
        ...state,
        isMasterAccountsProhibitionChecking: false,
        masterAccountsError: action.error,
        isMasterAccountsEnabled: null,
      };
    case UPDATE_MASTER_ACCOUNTS_PROHIBITION:
      return {
        ...state,
        isMasterAccountsProhibitionUpdating: true,
      };
    case UPDATE_MASTER_ACCOUNTS_PROHIBITION_SUCCESS:
      return {
        ...state,
        isMasterAccountsProhibitionUpdating: false,
        isMasterAccountsEnabled: action.data,
      };
    case UPDATE_MASTER_ACCOUNTS_PROHIBITION_ERROR:
      return {
        ...state,
        isMasterAccountsProhibitionUpdating: false,
        error: action.error,
        isMasterAccountsEnabled: null,
      };
    case FETCH_ASSOCIATED_ACCOUNTS:
      return {
        ...state,
        isAssociatedAccountsFetching: true,
      };
    case FETCH_ASSOCIATED_ACCOUNTS_SUCCESS:
      return {
        ...state,
        isAssociatedAccountsFetching: false,
        associatedAccounts: action.data,
        ssoLoginDefault: !hasDefaultSsoLogin(action.data),
      };
    case FETCH_ASSOCIATED_ACCOUNTS_ERROR:
      return {
        ...state,
        isAssociatedAccountsFetching: false,
        associatedAccountsError: action.error,
        associatedAccounts: null,
      };
    case TOGGLE_OFFER_POPUP:
      return {
        ...state,
        isOfferPopupOpened: action.data,
      };
    case CREATE_ACCOUNT_LINK:
      return {
        ...state,
        accountLinkingStatus: {
          isUpdating: true,
          isSent: false,
          success: false,
          error: null,
        },
      };
    case CREATE_ACCOUNT_LINK_SUCCESS:
      return {
        ...state,
        accountLinkingStatus: {
          isUpdating: false,
          isSent: false,
          success: true,
          error: null,
        },
      };
    case CREATE_ACCOUNT_LINK_ERROR:
      return {
        ...state,
        accountLinkingStatus: {
          isUpdating: false,
          isSent: false,
          success: false,
          error: action.error,
        },
      };
    case RESET_ACCOUNT_LINKING_STATUS:
      return {
        ...state,
        accountLinkingStatus: {
          isUpdating: false,
          isSent: false,
          success: false,
          error: null,
        },
      };
    case DELETE_ACCOUNT_LINK:
      return {
        ...state,
        isAccountLinkDeleting: true,
      };
    case DELETE_ACCOUNT_LINK_SUCCESS:
      return deleteAccountLink({
        ...state,
        isAccountLinkDeleting: false,
      }, action.data);
    case DELETE_ACCOUNT_LINK_ERROR:
      return {
        ...state,
        isAccountLinkDeleting: false,
        error: action.error,
      };
    case DELETE_MASTER_ACCOUNT_LINK:
      return {
        ...state,
        isMasterAccountLinkDeleting: true,
      };
    case DELETE_MASTER_ACCOUNT_LINK_SUCCESS:
      return deleteMasterAccountLink({
        ...state,
        isMasterAccountLinkDeleting: false,
      }, action.data);
    case DELETE_MASTER_ACCOUNT_LINK_ERROR:
      return {
        ...state,
        isMasterAccountLinkDeleting: false,
        error: action.error,
      };
    case SEND_ACCOUNT_LINK_REQUEST_SUCCESS:
      return {
        ...state,
        accountLinkingStatus: {
          isUpdating: false,
          isSent: true,
          success: false,
          error: null,
        },
      };
    case CANCEL_LINKAGE_REQUEST:
      return startCancelingOutgoingLinkageRequestForChild({
        ...state,
        isAccountLinkCanceling: true,
      }, action.data);
    case CANCEL_LINKAGE_REQUEST_FOR_MASTER:
      return startCancelingOutgoingLinkageRequestForMaster({
        ...state,
        isAccountLinkCanceling: true,
      }, action.data);
    case CANCEL_LINKAGE_REQUEST_SUCCESS:
      return cancelOutgoingLinkageRequestForChild({
        ...state,
        isAccountLinkCanceling: false,
      }, action.data);
    case CANCEL_LINKAGE_REQUEST_FOR_MASTER_SUCCESS:
      return cancelOutgoingLinkageRequestForMaster({
        ...state,
        isAccountLinkCanceling: false,
      }, action.data);
    case CANCEL_LINKAGE_REQUEST_ERROR:
      return {
        ...state,
        isAccountLinkCanceling: false,
        error: action.error,
      };
    case FETCH_LINKAGE_REQUESTS:
      return {
        ...state,
        isLinkageRequestsFetching: true,
      };
    case FETCH_LINKAGE_REQUESTS_SUCCESS:
      return {
        ...state,
        isLinkageRequestsFetching: false,
        linkageRequests: {
          ...state.linkageRequests,
          ...action.data,
        },
      };
    case FETCH_LINKAGE_REQUESTS_ERROR:
      return {
        ...state,
        isLinkageRequestsFetching: false,
        associatedAccountsError: action.error,
      };
    case FETCH_LINKAGE_REQUESTS_FOR_MASTER:
      return {
        ...state,
        isLinkageRequestsForMasterFetching: true,
      };
    case FETCH_LINKAGE_REQUESTS_FOR_MASTER_SUCCESS:
      return {
        ...state,
        isLinkageRequestsForMasterFetching: false,
        linkageRequests: {
          ...state.linkageRequests,
          ...action.data,
        },
      };
    case FETCH_LINKAGE_REQUESTS_FOR_MASTER_ERROR:
      return {
        ...state,
        isLinkageRequestsForMasterFetching: false,
        masterAccountsError: action.error,
      };
    case UPDATE_LINKAGE_REQUEST:
      return startCancelingIncomingLinkageRequestForChild({
        ...state,
        isLinkageRequestUpdating: true,
      }, action.data);
    case UPDATE_LINKAGE_REQUEST_SUCCESS:
      return cancelIncomingLinkageRequestForChild({
        ...state,
        isLinkageRequestUpdating: false,
      }, action.data);
    case UPDATE_LINKAGE_REQUEST_ERROR:
      return {
        ...state,
        isLinkageRequestUpdating: false,
        error: action.error,
      };
    case UPDATE_LINKAGE_REQUEST_FOR_MASTER:
      return startCancelingIncomingLinkageRequestForMaster({
        ...state,
        isLinkageRequestUpdating: true,
      }, action.data);
    case UPDATE_LINKAGE_REQUEST_FOR_MASTER_SUCCESS:
      return cancelIncomingLinkageRequestForMaster({
        ...state,
        isLinkageRequestUpdating: false,
      }, action.data);
    case FETCH_OFFER:
      return {
        ...state,
        isOfferFetching: true,
      };
    case FETCH_OFFER_SUCCESS:
      return {
        ...state,
        isOfferFetching: false,
        offer: action.data,
      };
    case FETCH_OFFER_ERROR:
      return {
        ...state,
        isOfferFetching: false,
        associatedAccountsError: action.error,
      };
    case FETCH_LOGIN_USER_TYPES:
      return {
        ...state,
        isLoginUserTypesFetching: true,
      };
    case FETCH_LOGIN_USER_TYPES_SUCCESS:
      return {
        ...state,
        isLoginUserTypesFetching: false,
        loginUserTypes: action.data,
      };
    case FETCH_LOGIN_USER_TYPES_ERROR:
      return {
        ...state,
        isLoginUserTypesFetching: false,
        error: action.error,
      };
    case RESET_LOGIN_USER_TYPES:
      return {
        ...state,
        loginUserTypes: null,
      };
    case UPDATE_ASSOCIATED_ACCOUNT:
      return {
        ...state,
        isAssociatedAccountUpdating: true,
      };
    case UPDATE_ASSOCIATED_ACCOUNT_ERROR:
      return {
        ...state,
        isAssociatedAccountUpdating: false,
        error: action.error,
      };
    case UPDATE_ASSOCIATED_CTN_ACCOUNT_SUCCESS:
      return updateAssociatedCtnAccount({
        ...state,
        isAssociatedAccountUpdating: false,
      }, action.data);
    case UPDATE_ASSOCIATED_BAN_ACCOUNT_SUCCESS:
      return updateAssociatedBanAccount({
        ...state,
        isAssociatedAccountUpdating: false,
      }, action.data);
    case UPDATE_ASSOCIATED_BEN_ACCOUNT_SUCCESS:
      return updateAssociatedBenAccount({
        ...state,
        isAssociatedAccountUpdating: false,
      }, action.data);
    case SET_MASTER_ACCOUNT_AS_DEFAULT:
      return {
        ...state,
        isDefaultSsoLoginUpdating: true,
      };
    case SET_MASTER_ACCOUNT_AS_DEFAULT_SUCCESS:
      return resetDefaultSsoLoginForAssociatedAccounts({
        ...state,
        isDefaultSsoLoginUpdating: false,
        ssoLoginDefault: true,
      }, action.data);
    case SET_MASTER_ACCOUNT_AS_DEFAULT_ERROR:
      return {
        ...state,
        isDefaultSsoLoginUpdating: false,
        error: action.error,
      };
    case RESET_ERROR:
      return {
        ...state,
        error: null,
      };
    case RESET_ASSOCIATED_ACCOUNTS_ERROR:
      return {
        ...state,
        associatedAccountsError: null,
      };
    case RESET_MASTER_ACCOUNTS_ERROR:
      return {
        ...state,
        masterAccountsError: null,
      };
    case FETCH_PIN_PUK:
      return {
        ...state,
        isPinPukFetching: true,
      };
    case FETCH_PIN_PUK_SUCCESS:
      return {
        ...state,
        isPinPukFetching: false,
        pinPukFetchingError: null,
        pinPuk: action.data,
      };
    case FETCH_PIN_PUK_ERROR:
      return {
        ...state,
        isPinPukFetching: false,
        pinPukFetchingError: action.error,
        pinPuk: null,
      };
    default:
      return state;
  }
}
